// @ts-nocheck
// CAMINHO: src/components/tasks/CaseDeadlines.tsx
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, PlusCircle, CalendarClock } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { format, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AddDeadlineModal } from './AddDeadlineModal';

// O tipo Task pode ser movido para um arquivo de tipos global (ex: src/types/index.ts)
export type Task = {
  id: string;
  created_at: string;
  title: string;
  due_date: string;
  status: 'Pendente' | 'Concluída' | 'Cancelada';
  priority: 'Alta' | 'Média' | 'Baixa';
  case_id: string;
  user_id?: string;
};

interface CaseDeadlinesProps {
  caseId: string;
}

export const CaseDeadlines = ({ caseId }: CaseDeadlinesProps) => {
  const [deadlines, setDeadlines] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  const fetchDeadlines = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('case_id', caseId)
        .order('due_date', { ascending: true });

      if (error) throw error;
      setDeadlines(data || []);
    } catch (err: any) {
      toast({
        title: "Erro ao buscar prazos",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [caseId, toast]);

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm("Tem certeza que deseja excluir este prazo?")) return;
    try {
      const { error } = await supabase.from('tasks').delete().eq('id', taskId);
      if (error) throw error;
      toast({ title: "Sucesso!", description: "Prazo excluído." });
      fetchDeadlines();
    } catch (err: any) {
      toast({ title: "Erro ao excluir prazo", description: err.message, variant: "destructive" });
    }
  };

  useEffect(() => {
    fetchDeadlines();
  }, [fetchDeadlines]);

  const getPriorityBadgeVariant = (priority: Task['priority']): "destructive" | "secondary" | "outline" => {
    switch (priority) {
      case 'Alta':
        return 'destructive';
      case 'Média':
        return 'secondary';
      case 'Baixa':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-4"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div>
        {deadlines.length > 0 ? (
          <ul className="space-y-3">
            {deadlines.map((deadline) => {
              const isOverdue = isPast(new Date(deadline.due_date)) && deadline.status === 'Pendente';
              return (
                <li key={deadline.id} className={`p-3 rounded-lg border ${isOverdue ? 'border-destructive/50 bg-destructive/10' : 'bg-muted/50'}`}>
                  <p className="font-semibold text-sm">{deadline.title}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className={`text-xs font-medium ${isOverdue ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {format(new Date(deadline.due_date), "'Vence em' dd/MM/yyyy", { locale: ptBR })}
                    </span>
                    <Badge variant={getPriorityBadgeVariant(deadline.priority)}>{deadline.priority}</Badge>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-center text-sm text-muted-foreground py-4">
            <CalendarClock className="mx-auto w-8 h-8 mb-2 text-gray-400" />
            Nenhum prazo registrado para este caso.
          </div>
        )}
      </div>
      <Button size="sm" className="w-full" onClick={() => setIsModalOpen(true)}>
        <PlusCircle className="w-4 h-4 mr-2" />
        Adicionar Prazo
      </Button>
      <AddDeadlineModal 
        caseId={caseId}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSuccess={fetchDeadlines}
      />
    </div>
  );
};